/**
 * A customer's "never send our water here" list — hooks + pure helpers (Phase 28 / task
 * 28.5, DEC-331 D-5; the EXCLUDED part of spec §61).
 *
 * GET / POST /v1/disposal-exclusions and POST /:id/end. Reads need VIEW_HAULER_CATALOG,
 * changes MANAGE_HAULER_CATALOG — the customer screen's own permissions. An exclusion WARNS,
 * it never blocks (D-14 A): the place stays pickable with a red badge and this reason.
 *
 * The place list is GET /v1/disposal-recommendations with no pickup: every usable place
 * (facilities and the company's drop-off locations), complete. Everyone who can manage the
 * catalog also holds its guard (ASSIGN_JOB).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { DisposalRecommendationsResponse } from '../jobs/destination-options';

/** SWD A2: WELL is a catalog disposal well this company authorized (0210). */
export type ExclusionKind = 'FACILITY' | 'LOCATION' | 'WELL';

export interface DisposalExclusion {
  readonly id: string;
  readonly haulerOperatorId: string;
  readonly kind: ExclusionKind;
  readonly targetId: string;
  readonly targetName: string | null;
  readonly reason: string;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly createdByName: string | null;
}

export interface AddExclusionBody {
  readonly haulerOperatorId: string;
  readonly target: { readonly kind: ExclusionKind; readonly id: string };
  readonly reason: string;
}

export interface ExclusionPlace {
  readonly value: string;
  readonly label: string;
}

const exclusionsKey = (haulerOperatorId: string): readonly string[] => [
  'disposal-exclusions',
  haulerOperatorId,
];

export function useDisposalExclusions(
  haulerOperatorId: string,
): ReturnType<typeof useApiQuery<{ exclusions: readonly DisposalExclusion[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ exclusions: readonly DisposalExclusion[] }>(
    opts,
    `/v1/disposal-exclusions?haulerOperatorId=${encodeURIComponent(haulerOperatorId)}`,
    { queryKey: exclusionsKey(haulerOperatorId), staleTime: 30_000 },
  );
}

/** Every usable place, for the "add" picker (no pickup → plain names). */
export function useExclusionPlaces(
  enabled: boolean,
): ReturnType<typeof useApiQuery<DisposalRecommendationsResponse>> {
  const opts = useApiOpts();
  return useApiQuery<DisposalRecommendationsResponse>(opts, '/v1/disposal-recommendations', {
    queryKey: ['disposal-recommendations', ''],
    staleTime: 60_000,
    enabled,
  });
}

function useInvalidateExclusions(haulerOperatorId: string): () => void {
  const qc = useQueryClient();
  return (): void => {
    void qc.invalidateQueries({ queryKey: exclusionsKey(haulerOperatorId) });
    // The New Job ranking marks excluded places; refresh it too.
    void qc.invalidateQueries({ queryKey: ['disposal-recommendations'] });
  };
}

export function useAddDisposalExclusion(
  haulerOperatorId: string,
): ReturnType<typeof useApiMutation<AddExclusionBody, { id: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateExclusions(haulerOperatorId);
  return useApiMutation<AddExclusionBody, { id: string }>(opts, '/v1/disposal-exclusions', 'POST', {
    onSuccess: invalidate,
  });
}

export function useEndDisposalExclusion(
  haulerOperatorId: string,
  exclusionId: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { ok: boolean }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateExclusions(haulerOperatorId);
  return useApiMutation<Record<string, never>, { ok: boolean }>(
    opts,
    `/v1/disposal-exclusions/${exclusionId}/end`,
    'POST',
    { onSuccess: invalidate },
  );
}

/** Places that can still be added: every place A→Z, minus those already excluded. */
export function exclusionPlaceOptions(
  places: DisposalRecommendationsResponse | undefined,
  active: readonly DisposalExclusion[],
): ExclusionPlace[] {
  if (places === undefined) return [];
  const taken = new Set(active.map((x) => `${x.kind}:${x.targetId}`));
  return places.options
    .filter((o) => !taken.has(`${o.kind}:${o.id}`))
    .map((o) => ({
      value: `${o.kind}:${o.id}`,
      label:
        o.kind === 'LOCATION'
          ? `${o.name} (your drop-off location)`
          : o.kind === 'WELL'
            ? `${o.name} (your disposal well)`
            : o.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The POST body, or a message saying what is missing. */
export function buildExclusionBody(
  haulerOperatorId: string,
  placeValue: string,
  reason: string,
): { ok: true; body: AddExclusionBody } | { ok: false; message: string } {
  const i = placeValue.indexOf(':');
  const kind = i < 0 ? '' : placeValue.slice(0, i);
  const id = i < 0 ? '' : placeValue.slice(i + 1);
  if ((kind !== 'FACILITY' && kind !== 'LOCATION' && kind !== 'WELL') || id === '') {
    return { ok: false, message: 'Pick a place.' };
  }
  const r = reason.trim();
  if (r.length < 3) return { ok: false, message: 'Say why (at least 3 characters).' };
  return { ok: true, body: { haulerOperatorId, target: { kind, id }, reason: r } };
}
